import $ from 'jquery';
import { attributeToggler, getSiblingElements } from '../utils/dom.utils';

const toggleAttributeName = 'data-stack-nav-toggle';

const optionSelector = 'li.nobu-nav-option';
const backgroundSelector = 'div.nobu-nav-background';
const stackSelector = 'div.nobu-nav-stack';
const backButtonsSelector = '.nobu-nav-toggle-back';
const closeButtonsSelector = '.nobu-nav-toggle-close';
const allTabbableElementsSelector = "button, a, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])";

const stacksActiveClassName = "n-stacks-active";
const openedMenuClassName = 'nobu-nav-open';
const openedMenuLeftClassName = 'nobu-nav-open-left';
const openedMenuRightClassName = 'nobu-nav-open-right';
const openedMenuLeftSecondClassName = 'nobu-nav-open-left-second';
const activeNavClassName = 'nobu-nav-active';
const activeNavBodyClassName = 'n-stacks-on';
const captionShadowClassName = 'nobu-nav-caption-shadow';

interface StacksElements {
    root: HTMLElement;
    background: HTMLElement | null;
    options: HTMLElement[];
    stacks: HTMLElement[];
    backButtons: HTMLElement[];
    closeButtons: HTMLElement[];
    toggleButtons: HTMLElement[];
    tabbableElements: HTMLElement[];
};

class Stacks {
    public readonly id: string;
    private elements: StacksElements;
    private activeOptionTree: HTMLElement[] | null = null;
    private menuIsOpen: boolean = false;
    private side: 'left' | 'right' | null = null;
    private activationTimeout: number | null = null;
    private attributeToggler: ReturnType<typeof attributeToggler> | null = null;
    private currentTriggerButton: HTMLElement | null = null;

    constructor(
        stackNavElement: HTMLElement,
        id: string
    ) {
        this.id = id;
        this.elements = {
            root: stackNavElement,
            background: stackNavElement.querySelector(backgroundSelector),
            options: Array.from(stackNavElement.querySelectorAll(optionSelector)),
            stacks: Array.from(stackNavElement.querySelectorAll(stackSelector)),
            backButtons: Array.from(stackNavElement.querySelectorAll(backButtonsSelector)),
            closeButtons: Array.from(stackNavElement.querySelectorAll(closeButtonsSelector)),
            toggleButtons: Array.from(document.querySelectorAll('[' + toggleAttributeName + '=' + id + ']')),
            tabbableElements: Array.from(stackNavElement.querySelectorAll(allTabbableElementsSelector)),
        };
        this.activeOptionTree = null;
        this.side = (
            stackNavElement.querySelector('.nobu-nav-content-left')
                ? 'left'
                : (
                    stackNavElement.querySelector('.nobu-nav-content-right')
                        ? 'right'
                        : null
                )
        );
        this.initUI();
    }

    private initUI() {
        const self = this;
        const { elements } = self;

        elements.backButtons.forEach(function (backButtonElements: HTMLElement) {
            backButtonElements.addEventListener('click', function (event) {
                event.preventDefault();
                event.stopPropagation();
                self.activateParentOption();
            });
        });

        elements.closeButtons.forEach(function (closeButtonElement: HTMLElement) {
            closeButtonElement.addEventListener('click', function (event) {
                event.preventDefault();
                event.stopPropagation();
                self.close();
            });
        });

        elements.toggleButtons.forEach(function (toggleElement: HTMLElement) {
            const activeOptionIndex = toggleElement.hasAttribute('data-stack-nav-toggle-active')
                ? (function (index) {
                    return isNaN(index) ? null : index
                })(Number(toggleElement.getAttribute('data-stack-nav-toggle-active')))
                : null;

            let activationFromKeyboard = false;

            toggleElement.addEventListener("keypress", (event) => {
                activationFromKeyboard = (event.key === "Enter") || (event.key === " ");
            });

            toggleElement.addEventListener('click', function (event) {
                event.preventDefault();
                event.stopPropagation();
                self.currentTriggerButton = toggleElement;
                self.toggle(
                    activationFromKeyboard ? null : activeOptionIndex
                );
                activationFromKeyboard = false;
            });
        });

        elements.options.forEach(function (optionElement) {
            const linkElement = optionElement.querySelector<HTMLLinkElement>('& > a');
            const buttonElement = optionElement.querySelector<HTMLButtonElement>('& > button');
            const stackElement = optionElement.querySelector('div.nobu-nav-stack');
            const subOptionsElement = optionElement.querySelector('ul.nobu-nav-options');

            if (linkElement) {
                linkElement.addEventListener('click', function () {
                    setTimeout(self.close, 100);
                });
            }
            else if (buttonElement && (stackElement || subOptionsElement)) {
                buttonElement.onclick = function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                    self.activateOption(optionElement);
                };
            }
        });

        elements.stacks.forEach(function (stackElement) {
            const stackCaptionElement = stackElement.querySelector('div.nobu-nav-caption');

            if (stackCaptionElement) {
                stackElement.onscroll = function (event) {
                    event.stopPropagation();

                    if (stackElement.scrollTop > 4) {
                        stackCaptionElement.classList.add(captionShadowClassName);
                    }
                    else {
                        stackCaptionElement.classList.remove(captionShadowClassName);
                    }
                };
            }
        });

        if (elements.background) {
            elements.background.addEventListener('click', function (event) {
                event.preventDefault();
                event.stopPropagation();
                self.close();
            });
        }

        if (elements.root.classList.contains(openedMenuClassName)) {
            self.open(0);
        }

        document.addEventListener('keydown', (event) => {
            if (!self.menuIsOpen) return;
            const { key, shiftKey } = event;
            const { activeElement } = document;


            switch (key) {
                case 'Escape':
                    self.close();
                    break;
                case 'ArrowLeft':
                    (self.side === 'left') && self.activateOption(null);
                    break;
                case 'ArrowRight':
                    (self.side === 'right') && self.activateOption(null);
                    break;
                case 'Tab':
                    const focusedElement = activeElement as HTMLElement | null;
                    if (shiftKey && (focusedElement === elements.tabbableElements[0])) {
                        event.preventDefault();
                        self.focusOption('last');
                    }
                    else if (!shiftKey && (focusedElement === elements.tabbableElements[elements.tabbableElements.length - 1])) {
                        event.preventDefault();
                        self.focusOption('first');
                    }
                    break;
            }
        });
    }

    private activateParentOption() {
        if (this.activeOptionTree && this.activeOptionTree.length > 2) {
            this.activateOption(this.activeOptionTree[this.activeOptionTree.length - 2]);
        }
        else {
            this.activateOption(null);
        }
    }

    private activateOption(optionElement: HTMLElement | null) {
        if (this.activeOptionTree) {
            this.activeOptionTree.forEach(function (activeOption) {
                activeOption.classList.remove(activeNavClassName);
                
                const button = activeOption.querySelector("& > button");

                if (button) {
                    button.setAttribute('aria-expanded', 'false');
                }
            });

            this.activeOptionTree = null;
        }

        if (optionElement) {
            this.activeOptionTree = this.getOptionTreeUp(optionElement);
            this.activeOptionTree.forEach(function (activeOption) {
                activeOption.classList.add(activeNavClassName);

                const button = activeOption.querySelector("& > button");

                if (button) {
                    button.setAttribute("aria-expanded", "true");
                }
            });
        }
    }

    private getOptionTreeUp(optionElement: HTMLElement) {
        var tree = [optionElement];

        $(optionElement).parents(optionSelector).each(function () {
            tree.push(this);
        });

        return tree;
    }

    private resetStackScrolls() {
        this.elements.stacks.forEach(stackElement => {
            stackElement.querySelector('div.nobu-nav-caption')?.classList.remove(captionShadowClassName);
            stackElement.scrollTop = 0;
        });
    }

    private updateAriaProps() {
        const {
            menuIsOpen,
            elements: {
                root,
                toggleButtons,
                backButtons,
                closeButtons,
            }
        } = this;
        const ariaExpanded = menuIsOpen ? 'true' : 'false';
        const ariaHidden = menuIsOpen ? 'false' : 'true';

        [
            ...toggleButtons,
            ...backButtons,
            ...closeButtons,
        ].forEach(element => {
            element.setAttribute('aria-expanded', ariaExpanded);
        });

        root.setAttribute('aria-hidden', ariaHidden);

        if (menuIsOpen) {
            this.attributeToggler = attributeToggler(getSiblingElements(root), {
                'aria-hidden': ariaExpanded,
            }).apply();

        }
        else if (this.attributeToggler) {
            this.attributeToggler.restore();
            this.attributeToggler = null;
        }
    }

    private focusOption(firstOrLast: 'first' | 'last') {
        const {
            elements: {
                tabbableElements
            }
        } = this;

        if (firstOrLast === 'first') {
            tabbableElements[0]?.focus();
            // tabbableElements[0]?.blur();
        }
        else {
            tabbableElements[tabbableElements.length - 1]?.focus();
            // tabbableElements[tabbableElements.length - 1]?.blur();
        }
    }

    private activate(activated: boolean, callback: (self: this) => void) {
        if (this.activationTimeout !== null) {
            window.clearTimeout(this.activationTimeout);
            this.activationTimeout = null;
        }

        if (activated) {
            this.elements.root.classList.add(stacksActiveClassName);
            this.activationTimeout = window.setTimeout(() => {
                this.activationTimeout = null;
                callback(this);
            }, 0);
        }
        else {
            callback(this);
            this.activationTimeout = window.setTimeout(() => {
                this.activationTimeout = null;
                this.elements.root.classList.remove(stacksActiveClassName);
            }, 500);
        }
    }

    public open(selectedOptionIndex?: number | null) {
        this.activate(true, self => {
            const { elements } = self;

            self.menuIsOpen = true;
            document.documentElement.classList.add(activeNavBodyClassName);
            document.body.classList.add(activeNavBodyClassName);
            elements.root.classList.add(openedMenuClassName);

            switch (self.side) {
                case 'left':
                    elements.root.classList.add(openedMenuLeftClassName);
                    break;

                case 'right':
                    elements.root.classList.add(openedMenuRightClassName);
                    break;
            }

            setTimeout(() => {
                self.focusOption('first');
            }, 200);

            if (
                (window.innerWidth > 576)
                && (selectedOptionIndex !== undefined)
                && (selectedOptionIndex !== null)
                && (selectedOptionIndex > -1)
            ) {
                self.activateOption(elements.options[selectedOptionIndex]!);
            }

            self.updateAriaProps();
            self.resetStackScrolls();
        });
    }

    public close() {
        this.activate(false, self => {
            const {
                elements: {
                    root: rootElement,
                },
            } = self;

            if (self.activeOptionTree) {
                self.activateOption(null);
            }

            self.menuIsOpen = false;
            document.body.classList.remove(activeNavBodyClassName);
            document.documentElement.classList.remove(activeNavBodyClassName);
            rootElement.classList.remove(openedMenuClassName);
            rootElement.classList.remove(openedMenuLeftClassName);
            rootElement.classList.remove(openedMenuRightClassName);
            rootElement.classList.remove(openedMenuLeftSecondClassName);

            setTimeout(() => {
                self.currentTriggerButton?.focus();
                self.currentTriggerButton = null;
            }, 200);

            self.updateAriaProps();
            self.resetStackScrolls();
        });
    }

    public toggle(activeOptionIndex?: number | null) {
        if (this.menuIsOpen) {
            this.close();
        }
        else {
            this.open(activeOptionIndex);
        }
    }
}

export default Stacks;
